import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, Chip, InputAdornment, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Close from "@mui/icons-material/Close";
import LinkedIn from "@mui/icons-material/LinkedIn";
import MailOutlined from "@mui/icons-material/MailOutlined";
import MergeType from "@mui/icons-material/MergeType";
import Verified from "@mui/icons-material/Verified";
import ManageSearch from "@mui/icons-material/ManageSearch";
import CompareArrows from "@mui/icons-material/CompareArrows";
import TaskAlt from "@mui/icons-material/TaskAlt";
import { FaClone } from "react-icons/fa";
import { withSnackbar } from "../../utils/SnackbarHOC";
import DuplicateService from "../../services/DuplicateService";
import { AppButton } from "../../components/UI";
import MergeDialog from "./MergeDialog";

const fullName = (c) => `${c.prenom || ""} ${c.nom || ""}`.trim() || "(sans nom)";
const initials = (c) => `${c.prenom?.[0] || ""}${c.nom?.[0] || ""}`.toUpperCase() || "?";
const normalize = (s) => String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const REASONS = {
  linkedin: { label: "LinkedIn", Icon: LinkedIn, color: "#03407b", bg: "#ebf4fb", border: "#aed6f1" },
  email: { label: "Email", Icon: MailOutlined, color: "#b45309", bg: "#fffbeb", border: "#fcd34d" },
};
const reasonOf = (g) => (g.reason === "email" ? REASONS.email : REASONS.linkedin);

const COUNTS = [
  { key: "processes", label: "process" },
  { key: "tasks", label: "tâches" },
  { key: "notes", label: "notes" },
  { key: "files", label: "CV" },
  { key: "selections", label: "sél." },
];

// Cache de session : garde le résultat du scan lors d'un rafraîchissement de la
// page (F5). sessionStorage est vidé à la fermeture de l'onglet.
const CACHE_KEY = "doublons_scan_result";

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeCache = (groups) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ groups, at: Date.now() }));
  } catch (e) {
    // quota dépassé ou storage indisponible : on ignore, le cache est optionnel
  }
};

const card = { borderRadius: "14px", background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.05)" };

const StatCard = ({ label, value, Icon, color = "#03407b", bg = "#ebf4fb", active, onClick }) => (
  <Box
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={(e) => onClick && (e.key === "Enter" || e.key === " ") && onClick()}
    sx={{
      flex: "1 1 170px", display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1.25, borderRadius: "12px",
      cursor: onClick ? "pointer" : "default", background: active ? bg : "#fff",
      border: `1.5px solid ${active ? color : "#e2e8f0"}`, transition: "all 0.15s ease",
      "&:hover": onClick ? { borderColor: color } : undefined,
    }}
  >
    <Box sx={{ width: 34, height: 34, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", background: bg, color }}>
      <Icon sx={{ fontSize: 19 }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontSize: "0.76rem", color: "#64748b" }}>{label}</Typography>
    </Box>
  </Box>
);

// Shown before the first scan: what the tool does, in 3 steps.
const Intro = ({ onScan, loading }) => (
  <Box sx={{ ...card, p: { xs: 2.5, md: 4 }, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2.5 }}>
    <Box sx={{ width: 64, height: 64, borderRadius: "18px", display: "flex", alignItems: "center", justifyContent: "center", background: "#ebf4fb", color: "#03407b" }}>
      <FaClone size={28} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a" }}>Nettoyez le vivier en fusionnant les doublons</Typography>
      <Typography sx={{ fontSize: "0.88rem", color: "#64748b", mt: 0.5, maxWidth: 560 }}>
        Un même candidat peut avoir été créé plusieurs fois (import, extension, saisie manuelle). Le scan les retrouve pour vous.
      </Typography>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.5, width: "100%", maxWidth: 820 }}>
      {[
        { Icon: ManageSearch, title: "1. Scanner", text: "Compare tous les candidats par profil LinkedIn (référence) puis par email." },
        { Icon: CompareArrows, title: "2. Comparer", text: "Pour chaque groupe, voyez les fiches côte à côte avec leurs process, notes et CV." },
        { Icon: MergeType, title: "3. Fusionner", text: "Gardez la fiche la plus complète : l'historique des autres y est rattaché." },
      ].map(({ Icon, title, text }) => (
        <Box key={title} sx={{ p: 1.75, borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0", textAlign: "left" }}>
          <Icon sx={{ color: "#03407b", fontSize: 22, mb: 0.5 }} />
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#64748b", lineHeight: 1.45 }}>{text}</Typography>
        </Box>
      ))}
    </Box>
    <AppButton variant="primary" onClick={onScan} loading={loading} startIcon={<ManageSearch fontSize="small" />}>
      Lancer le scan des doublons
    </AppButton>
  </Box>
);

const CandidateRow = ({ c, keeper }) => (
  <Box sx={{
    display: "flex", alignItems: "center", gap: 1.25, p: 1.25, borderRadius: "10px", flexWrap: "wrap",
    background: keeper ? "#f0fdf4" : "#f8fafc", border: `1px solid ${keeper ? "#bbf7d0" : "#f1f5f9"}`,
  }}>
    <Avatar sx={{ width: 32, height: 32, fontSize: "0.75rem", fontWeight: 700, bgcolor: keeper ? "#dcfce7" : "#ebf4fb", color: keeper ? "#166534" : "#03407b" }}>
      {initials(c)}
    </Avatar>
    <Box sx={{ minWidth: 180, flex: "1 1 220px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#0f172a" }}>{fullName(c)}</Typography>
        {keeper && (
          <Tooltip title="Fiche la plus complète : proposée comme fiche à conserver lors de la fusion." arrow>
            <Chip icon={<Verified sx={{ fontSize: "14px !important", color: "#166534 !important" }} />} label="À conserver" size="small"
              sx={{ height: 20, fontSize: "0.66rem", fontWeight: 700, color: "#166534", background: "#dcfce7" }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: "0.75rem", color: "#64748b", wordBreak: "break-all" }}>
        {c.email || "Sans email"} · {c.linkedin || "Sans LinkedIn"}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {COUNTS.map(({ key, label }) => {
        const n = c.counts?.[key] ?? 0;
        return (
          <Chip key={key} size="small" label={`${n} ${label}`}
            sx={{ height: 20, fontSize: "0.66rem", fontWeight: 600, color: n ? "#03407b" : "#94a3b8", background: n ? "#ebf4fb" : "#f1f5f9" }} />
        );
      })}
    </Box>
  </Box>
);

const Doublons = ({ snackbarShowMessage }) => {
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [scannedAt, setScannedAt] = useState(null);
  const [reasonFilter, setReasonFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Restaure le dernier scan de la session au chargement de la page.
  useEffect(() => {
    const cached = readCache();
    if (cached && Array.isArray(cached.groups)) {
      setGroups(cached.groups);
      setScannedAt(cached.at || null);
      setScanned(true);
    }
  }, []);

  const runScan = async () => {
    try {
      setLoading(true);
      const res = await DuplicateService.scanDuplicates();
      const nextGroups = res.data.groups || [];
      setGroups(nextGroups);
      setScanned(true);
      setScannedAt(Date.now());
      writeCache(nextGroups);
    } catch (e) {
      snackbarShowMessage?.("Échec du scan des doublons.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleMerged = (group) => {
    // Retire le groupe fusionné de la liste et met à jour le cache.
    setGroups((prev) => {
      const next = prev.filter((g) => g !== group);
      writeCache(next);
      return next;
    });
  };

  const totalDuplicates = groups.reduce((acc, g) => acc + Math.max(0, g.candidats.length - 1), 0);
  const byEmail = groups.filter((g) => g.reason === "email").length;

  const visibleGroups = useMemo(() => {
    const q = normalize(search.trim());
    return groups
      .filter((g) => reasonFilter === "all" || (reasonFilter === "email") === (g.reason === "email"))
      .filter((g) => !q || g.candidats.some((c) => [fullName(c), c.email, c.linkedin].some((v) => normalize(v).includes(q))));
  }, [groups, reasonFilter, search]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: "#ebf4fb", color: "#03407b" }}>
            <FaClone size={18} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>Doublons candidats</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "#64748b" }}>
              Détection par profil LinkedIn (référence) puis par email
              {scanned && scannedAt && ` · dernier scan le ${new Date(scannedAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}`}
            </Typography>
          </Box>
        </Box>
        {scanned && (
          <Box sx={{ ml: { md: "auto" } }}>
            <AppButton variant="secondary" size="small" onClick={runScan} loading={loading} startIcon={<ManageSearch fontSize="small" />}>
              {loading ? "Analyse en cours…" : "Relancer le scan"}
            </AppButton>
          </Box>
        )}
      </Box>

      {!scanned && !loading && <Intro onScan={runScan} loading={loading} />}

      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#03407b", fontSize: "0.85rem", fontWeight: 600 }}>
            <ManageSearch sx={{ fontSize: 18 }} /> Analyse de tous les candidats du vivier… cela peut prendre quelques secondes.
          </Box>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={150} sx={{ borderRadius: "14px" }} />)}
        </Box>
      )}

      {scanned && !loading && (
        groups.length === 0 ? (
          <Box sx={{ ...card, p: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, textAlign: "center" }}>
            <TaskAlt sx={{ fontSize: 44, color: "#16a34a" }} />
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>Aucun doublon détecté</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>Le vivier est propre. Relancez le scan après un import de candidats.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <StatCard label="Groupes de doublons" value={groups.length} Icon={FaCloneIcon} active={reasonFilter === "all"} onClick={() => setReasonFilter("all")} />
              <StatCard label="Fiches en trop à fusionner" value={totalDuplicates} Icon={MergeType} color="#b91c1c" bg="#fef2f2" />
              <StatCard label="Détectés par LinkedIn" value={groups.length - byEmail} Icon={LinkedIn}
                active={reasonFilter === "linkedin"} onClick={() => setReasonFilter(reasonFilter === "linkedin" ? "all" : "linkedin")} />
              <StatCard label="Détectés par email" value={byEmail} Icon={MailOutlined} color="#b45309" bg="#fffbeb"
                active={reasonFilter === "email"} onClick={() => setReasonFilter(reasonFilter === "email" ? "all" : "email")} />
            </Box>

            <TextField
              size="small"
              placeholder="Rechercher un nom, un email, un LinkedIn…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ maxWidth: 420, "& .MuiOutlinedInput-root": { borderRadius: "10px", background: "#fff" } }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8" }} /></InputAdornment>,
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <Close onClick={() => setSearch("")} sx={{ fontSize: 18, color: "#94a3b8", cursor: "pointer" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {visibleGroups.length === 0 ? (
              <Box sx={{ ...card, p: 4, textAlign: "center", color: "#64748b", fontSize: "0.88rem" }}>
                Aucun groupe ne correspond à ce filtre.
              </Box>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 1.5 }}>
                {visibleGroups.map((group, idx) => {
                  const keeper = group.candidats.find((c) => c.isSuggestedKeeper) || group.candidats[0];
                  const r = reasonOf(group);
                  return (
                    <Box key={idx} sx={{ ...card, p: 1.75, display: "flex", flexDirection: "column", gap: 1.25 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip icon={<r.Icon sx={{ fontSize: "15px !important", color: `${r.color} !important` }} />} label={`Même ${r.label}`} size="small"
                          sx={{ height: 24, fontWeight: 700, fontSize: "0.72rem", color: r.color, background: r.bg, border: `1px solid ${r.border}` }} />
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155" }}>
                          {group.candidats.length} fiches pour {fullName(keeper)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                        {group.candidats.map((c) => <CandidateRow key={c.id} c={c} keeper={c.id === keeper.id} />)}
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <AppButton variant="primary" size="small" startIcon={<CompareArrows fontSize="small" />} onClick={() => setActiveGroup(group)}>
                          Comparer et fusionner
                        </AppButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )
      )}

      {activeGroup && (
        <MergeDialog
          open={Boolean(activeGroup)}
          group={activeGroup}
          onClose={() => setActiveGroup(null)}
          onMerged={handleMerged}
          snackbarShowMessage={snackbarShowMessage}
        />
      )}
    </Box>
  );
};

// MUI-style wrapper so the react-icons clone icon accepts the same `sx` as the stat icons.
function FaCloneIcon({ sx }) {
  return <Box component="span" sx={{ display: "flex", ...sx }}><FaClone size={16} /></Box>;
}

export default withSnackbar(Doublons);
